import React from 'react';
import { Row } from 'vetrf-ui/dist/utils/type/profile-table/Row';
import { CatalogLink } from 'modules/catalogs/components/CatalogLink';
import { ErulRegistrationCodes } from 'api/erul/types';
import { CerberusIntegrationStatus } from 'components/CerberusIntegrationStatus';
import { CerberusIntegrationError } from 'components/CerberusIntegrationError';
import { getMessageForElkEpgu } from 'api/elkEpgu/utils';

export const mainBlockColumns: Row[] = [
  {
    dataFormatter: (item) => item.registrationNumber,
    header: {
      key: 'registrationNumber',
      title: 'Регистрационный номер',
    },
  },
  {
    dataFormatter: (item) => item.registerEntryDate,
    header: {
      key: 'registerEntryDate',
      title: 'Дата внесения в реестр',
    },
  },
  {
    dataFormatter: (item) => item.gmpValidityPeriodDateStart,
    header: {
      key: 'gmpValidityPeriodDateStart',
      title: 'Дата начала действия заключения',
    },
  },
  {
    dataFormatter: (item) => item.gmpValidityPeriodDateEnd,
    header: {
      key: 'gmpValidityPeriodDateEnd',
      title: 'Дата окончания действия заключения',
    },
  },
  {
    dataFormatter: (item) => (
      <a href={`/files/${item.gmpConclusionFile?.id}`}>{item.gmpConclusionFile?.name}</a>
    ),
    header: {
      key: 'gmpConclusionFile',
      title: 'Файл заключения GMP',
    },
  },
  {
    dataFormatter: (item) => item?.dataDate?.split(' ')[0],
    header: {
      key: 'dataDate',
      title: 'Дата ввода сведений',
    },
  },
  {
    dataFormatter: (item) => item?.status?.name,
    header: {
      key: 'status',
      title: 'Статус',
    },
  },
];

export const erulBlockColumns: Row[] = [
  {
    dataFormatter: (item) => item.erulRegNumber,
    header: {
      key: 'erulRegNumber',
      title: 'Регистрационный номер ЕРУЛ',
    },
  },
  {
    dataFormatter: (item) => item.erulRegDate,
    header: {
      key: 'erulRegDate',
      title: 'Дата получения регистрационного номера ЕРУЛ',
    },
  },
];

export const erulWithStatusBlockColumns: Row[] = [
  {
    dataFormatter: (item) =>
      !!item.erulRegNumber &&
      `${item.erulRegNumber}${
        !item.erulManualChanged &&
        item.erulRegistration?.status.code === ErulRegistrationCodes.ERUL_SUCCESS
          ? ' (получен автоматически)'
          : ''
      }`,
    header: {
      key: 'erulRegNumber',
      title: 'Регистрационный номер ЕРУЛ',
    },
  },
  {
    dataFormatter: (item) =>
      !!item.erulRegDate &&
      `${item.erulRegDate}${
        !item.erulManualChanged &&
        item.erulRegistration?.status.code === ErulRegistrationCodes.ERUL_SUCCESS
          ? ' (получен автоматически)'
          : ''
      }`,
    header: {
      key: 'erulRegDate',
      title: 'Дата получения регистрационного номера ЕРУЛ',
    },
  },
];

export const producerBlockColumns: Row[] = [
  {
    dataFormatter: (item) => item.producerRegNumber,
    header: {
      key: 'producerRegNumber',
      title: 'Регистрационный номер',
    },
  },
  {
    dataFormatter: (item) => (
      <span>
        <CatalogLink
          target="_blank"
          rel="noreferrer"
          href={`/#/catalogs/business-entities/${item.id}`}
        >
          {item.producerName}
        </CatalogLink>
        <CerberusIntegrationStatus isNotLinkedWithCerberus={item.notLinkedWithCerberus} />
        <CerberusIntegrationError showMessage={item.incorrectCerberusLink} />
      </span>
    ),
    header: {
      key: 'producerName',
      title: 'Производитель',
    },
  },
  {
    dataFormatter: (item) => item.producerAddress,
    header: {
      key: 'producerAddress',
      title: 'Адрес производителя',
    },
  },
  {
    dataFormatter: (item) => item.site,
    header: {
      key: 'site',
      title: 'Наименование площадки',
    },
  },
  {
    dataFormatter: (item) => item.siteAddress,
    header: {
      key: 'siteAddress',
      title: 'Адрес площадки',
    },
  },
];

export const licenseBlockColumns: Row[] = [
  {
    dataFormatter: (item) => item.licenseNumber,
    header: {
      key: 'licenseNumber',
      title: 'Номер',
    },
  },
  {
    dataFormatter: (item) => item.licenseDate,
    header: {
      key: 'licenseDate',
      title: 'Дата выдачи',
    },
  },
];

export const inspectionBlockColumns: Row[] = [
  {
    dataFormatter: (item) => (
      <>
        {item.inspectionInitiator?.name}
        <CerberusIntegrationStatus isNotLinkedWithCerberus={item.notLinkedWithCerberus} />
        <CerberusIntegrationError showMessage={item.incorrectCerberusLink} />
      </>
    ),
    header: {
      key: 'inspectionInitiator',
      title: 'Уполномоченный орган, инициировавший фармацевтическую инспекцию',
    },
  },
  {
    dataFormatter: (item) => item.inspectionDate,
    header: {
      key: 'inspectionDate',
      title: 'Дата проведения фармацевтической инспекции',
    },
  },
  {
    dataFormatter: (item) => item.dosageForm?.name,
    header: {
      key: 'dosageForm',
      title: 'Лекарственная форма',
    },
  },
];

export const reportBlockColumns: Row[] = [
  {
    dataFormatter: (item) => item.name,
    header: {
      key: 'name',
      title: 'Наименование документа',
    },
  },
  {
    dataFormatter: (item) => item.number,
    header: {
      key: 'number',
      title: 'Номер документа',
    },
  },
  {
    dataFormatter: (item) => item.date,
    header: {
      key: 'date',
      title: 'Дата документа',
    },
  },
  {
    dataFormatter: (item) => {
      const { file } = item;
      if (file?.id) {
        return <a href={`/files/${file.id}`}>{file.name}</a>;
      }
    },
    header: {
      key: 'file',
      title: 'Файл',
    },
  },
];

export const rshnBlockColumns: Row[] = [
  {
    dataFormatter: (item) => item.rshnOrderNumber,
    header: {
      key: 'rshnOrderNumber',
      title: 'Номер приказа',
    },
  },
  {
    dataFormatter: (item) => item.rshnOrderDate,
    header: {
      key: 'rshnOrderDate',
      title: 'Дата',
    },
  },
];

export const requestBlockColumns: Row[] = [
  {
    dataFormatter: (item) => item.requestRegNumber,
    header: {
      key: 'requestRegNumber',
      title: 'Номер заявления',
    },
  },
  {
    dataFormatter: (item) => item.requestRegDate,
    header: {
      key: 'requestRegDate',
      title: 'Дата регистрации заявления',
    },
  },
  {
    dataFormatter: (item) => item.requestMethod?.name,
    header: {
      key: 'requestMethod',
      title: 'Способ подачи заявления',
    },
  },
];

export const epguRegistrationBlockColumn: Row = {
  dataFormatter: (item) => getMessageForElkEpgu(item.epguRegistration),
  header: {
    key: 'epguRegistration',
    title: 'Передача в ЕЛК ЕПГУ статуса заявления',
  },
};

export const preparationsBlockColumns: Row[] = [
  {
    dataFormatter: (item) =>
      item.registryProduct ? (
        <a href={`/#/registry/pharm/registry/${item.registryProduct.entry.id}`}>
          {item.registryProduct.name}
        </a>
      ) : (
        item.name
      ),
    header: {
      key: 'name',
      title: 'Название ЛС',
    },
  },
  {
    dataFormatter: (item) =>
      item.registryProduct ? item.registryProduct.entry.regNumber : item.regNumber,
    header: {
      key: 'regNumber',
      title: 'Регистрационный номер удостоверения',
    },
  },
];
